<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { api, type PricingSourceResponse, type PricingSourceFund } from '../api/client'
import { ragColour, ragLabel, pct, pickExampleFund, exampleSentence, fundDisplayLabel } from './pricingSourceHelpers'

// Stale-NAV pricing-source detector (spec 2026-09-25). Last block of the NAV
// tab (moved from Risk 2026-09-26). Fund-level data from GET
// /api/risk/pricing-source; which funds show is the host's `visibleFunds`.
// Titled "edge mechanics: is the price stale?" (Paolo 2026-10-26) as the
// companion of the Risk tab's SignalHealthBlock ("edge outcome: does the
// signal still pay?"); each legend cross-references the other because each is
// blind to the other's failure mode (live-priced NAV vs holdings drift).
const props = defineProps<{ visibleFunds: string[] }>()

const data = ref<PricingSourceResponse | null>(null)
const error = ref<string | null>(null)
const legendOpen = ref(false)
const exampleLabel = ref<string>('')
let timer: ReturnType<typeof setInterval> | null = null

async function load() {
  try {
    data.value = await api.pricingSource()
    error.value = null
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'unavailable'
  }
}

onMounted(() => { load(); timer = setInterval(load, 60_000) })
onUnmounted(() => { if (timer) clearInterval(timer) })

const funds = computed<PricingSourceFund[]>(() =>
  (data.value?.funds ?? []).filter(f => props.visibleFunds.includes(f.label)))

watch(funds, fs => {
  if (!fs.some(f => f.label === exampleLabel.value)) exampleLabel.value = pickExampleFund(fs)?.label ?? ''
}, { immediate: true })

// Live JH pass-through for the legend (JH shows a switch most slowly); any fund list, not just visible.
const jhPassThrough = computed(() => data.value?.funds?.find(f => f.label === 'JH')?.passThrough40 ?? null)

const exampleFund = computed(() => funds.value.find(f => f.label === exampleLabel.value) ?? null)
const example = computed(() => (exampleFund.value ? exampleSentence(exampleFund.value) : null))
</script>

<template>
  <div class="psd" data-testid="pricing-source-panel">
    <div class="psd-header">
      <h3 class="psd-title">Pricing source — edge mechanics: is the price stale?</h3>
      <button
        type="button"
        class="kt-info-btn"
        :aria-expanded="legendOpen"
        aria-controls="psd-legend"
        :aria-label="legendOpen ? 'Hide explanation' : 'Show explanation'"
        data-testid="psd-info-toggle"
        @click="legendOpen = !legendOpen"
      >i</button>
    </div>

    <section v-if="legendOpen" id="psd-legend" class="kt-legend" role="region"
      aria-label="Pricing source explanation" data-testid="psd-legend">
      <p class="kt-legend-lead"><b>What it measures.</b> How much of the overnight futures move the fund's
        valuer already puts into today's price. Each fund is struck at a fixed time (BGA 10:00, others 12:00 London)
        using mostly the previous US close, plus a partial fair-value adjustment. Whatever is <i>not</i> passed through
        is priced only at the next strike — that remainder is the strategy's edge.
        <b>Green</b> = normal for this fund. <b>Amber</b> = above normal, or not enough / stale data.
        <b>Red</b> = the NAV now passes through the full move, i.e. it appears to be priced at live prices and the edge
        is gone for that fund.</p>
      <p class="kt-legend-lead"><b>Companion section.</b> This measures how the NAV is struck, not whether the signal
        still pays. For that see <i>Signal health on the Risk tab</i>, which catches the holdings drifting off the
        reference future, a failure this section cannot see (a NAV that no longer follows the future looks
        <i>greener</i> here, not redder).</p>
      <p class="kt-legend-lead"><b>How it is calculated.</b> For each pair of official prices:
        <code>NAV move − close-based synthetic move = β × Δm</code>. Δm is the futures' move since the last US
        close measured at this strike, minus the same measure at the previous strike — i.e. the part of the futures
        move that is not yet in the US closing prices. β (the pass-through) is fitted by least squares over the last
        40 paired NAV days (an official price, a close-based synthetic and a futures print on the same day), and over
        20 as an early warning. Implausible NAV prints (bad data) are ignored and counted on the tile. Green if β40 ≤
        the fund's normal ceiling; Red only if both β40 and β20 reach the red floor. A fund that runs at k× its
        reference future (Tech 1.27× NQ, Miners 1.45× GC) saturates at β ≈ k when fully live-priced, so its fitted
        slope is divided by k first and the tile says <i>β-scaled</i>; 100% then means "the whole move" for every
        fund.</p>
      <table class="psd-thresholds">
        <thead><tr><th>Fund</th><th>Green if ≤</th><th>Red if ≥ (40 and 20 days)</th></tr></thead>
        <tbody>
          <tr v-for="f in funds" :key="'t' + f.label">
            <td>{{ fundDisplayLabel(f.label) }}</td><td>{{ pct(f.greenCeiling) }}</td><td>{{ pct(f.redFloor) }}</td>
          </tr>
        </tbody>
      </table>
      <p class="kt-legend-lead"><b>Worked example</b> (latest real day with a sizeable move):
        <select v-model="exampleLabel" data-testid="psd-example-fund" aria-label="Example fund">
          <option v-for="f in funds" :key="'o' + f.label" :value="f.label">{{ fundDisplayLabel(f.label) }}</option>
        </select>
      </p>
      <p class="psd-example" data-testid="psd-example">{{ example ?? 'No recent paired day for this fund yet.' }}</p>
      <p class="kt-legend-lead psd-note">Why now: US exchanges move to near-24-hour trading on 6 Dec 2026. If valuers
        switch to live prices, pass-through heads to 100%.
        <template v-if="jhPassThrough !== null">JH currently passes through {{ pct(jhPassThrough) }} of each move, so a
        switch shows more slowly there.</template>
        <template v-else>Funds that already pass through a large share show a switch more slowly.</template></p>
    </section>

    <div v-if="error" class="psd-error" data-testid="psd-error">Pricing-source data unavailable ({{ error }}).</div>

    <div class="psd-tiles">
      <div v-for="f in funds" :key="f.label" class="psd-tile" :data-testid="'psd-tile-' + f.label">
        <div class="psd-tile-head">
          <span class="psd-dot" :style="{ background: ragColour(f.rag) }"></span>
          <span class="psd-fund">{{ fundDisplayLabel(f.label) }}</span>
          <span class="psd-rag" :style="{ color: ragColour(f.rag) }">{{ ragLabel(f.rag) }}</span>
        </div>
        <div class="psd-metric">Passed through at strike: <b>{{ pct(f.passThrough40) }}</b></div>
        <div class="psd-metric">Edge left: <b>{{ pct(f.edgeLeft40) }}</b></div>
        <div class="psd-sub">20-day: {{ pct(f.passThrough20) }} · {{ f.n40 }} days · {{ f.latestDate ?? '—' }}</div>
        <div v-if="f.betaScale && f.betaScale !== 1" class="psd-sub" data-testid="psd-beta-scale">β-scaled ÷{{ f.betaScale.toFixed(2) }}
          (fund runs at {{ f.betaScale.toFixed(2) }}× {{ f.proxy }})</div>
        <div v-if="f.reason" class="psd-reason">{{ f.reason }}</div>
        <div v-if="f.excluded40" class="psd-reason">{{ f.excluded40 }} implausible NAV print{{ f.excluded40 === 1 ? '' : 's' }} ignored</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.psd { margin-top: 1.5rem; }
.psd-header { display: flex; align-items: center; gap: 0.5rem; }
.psd-title { margin: 0; font-size: 14px; color: #ccc; }
.psd-tiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 0.6rem; margin-top: 0.6rem; }
.psd-tile { background: #141414; border: 1px solid #2a2a2a; border-radius: 6px; padding: 0.6rem 0.7rem; font-size: 0.8rem; color: #ccc; }
.psd-tile-head { display: flex; align-items: center; gap: 0.4rem; margin-bottom: 0.35rem; }
.psd-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.psd-fund { font-weight: 600; color: #e6edf3; }
.psd-rag { margin-left: auto; font-weight: 600; }
.psd-sub { color: #888; margin-top: 0.25rem; }
.psd-reason { color: #fbc02d; margin-top: 0.25rem; }
.psd-error { color: #f44336; font-size: 0.8rem; margin-top: 0.4rem; }
.psd-example { background: #0f0f0f; border-left: 3px solid #58a6ff; padding: 0.5rem 0.7rem; margin: 0.3rem 0 0.6rem; }
.psd-note { color: #999; }
.psd-thresholds { border-collapse: collapse; margin: 0.2rem 0 0.6rem; font-size: 0.78rem; }
.psd-thresholds th, .psd-thresholds td { border: 1px solid #2a2a2a; padding: 0.15rem 0.5rem; text-align: left; }
/* kt-info-btn / kt-legend: same look as KtTrackingPanel's (i) legend (scoped copy). */
.kt-info-btn { display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; padding: 0;
  border-radius: 50%; background: transparent; color: #888; border: 1px solid #555;
  font-family: 'Times New Roman', Times, serif; font-style: italic; cursor: pointer; }
.kt-info-btn:hover, .kt-info-btn[aria-expanded="true"] { color: #e6edf3; border-color: #999; background: #ffffff10; }
.kt-info-btn:focus-visible { outline: 2px solid #58a6ff; outline-offset: 2px; }
.kt-legend { background: #141414; border: 1px solid #2a2a2a; border-radius: 6px; padding: 0.75rem 0.9rem;
  font-size: 0.8rem; color: #ccc; line-height: 1.5; margin-top: 0.5rem; }
.kt-legend-lead { margin: 0 0 0.65rem 0; color: #b3b3b3; }
</style>
